import PropTypes from 'prop-types';

/**
 * Safe timestamp to ISO string conversion
 */
const toISOStringSafe = (timestamp) => {
  if (!timestamp) return undefined;
  
  try {
    // Handle Firestore timestamp
    if (typeof timestamp === 'object' && typeof timestamp.seconds === 'number') {
      const date = new Date(timestamp.seconds * 1000);
      return isNaN(date.getTime()) ? undefined : date.toISOString();
    }
    
    // Handle Date object
    if (timestamp instanceof Date) {
      return isNaN(timestamp.getTime()) ? undefined : timestamp.toISOString();
    }
    
    // Handle timestamp object with toDate method
    if (typeof timestamp?.toDate === 'function') {
      const date = timestamp.toDate();
      return isNaN(date.getTime()) ? undefined : date.toISOString();
    }
    
    // Handle number timestamp
    if (typeof timestamp === 'number') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? undefined : date.toISOString();
    }

    // Handle string timestamp
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? undefined : date.toISOString();
    }

    return undefined;
  } catch (error) {
    console.warn('Invalid timestamp:', timestamp);
    return undefined;
  }
};

/**
 * Structured data component for rich search results
 * Helps Google understand your content and show rich snippets
 */
export default function StructuredData({ data }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, '\\u003c') }}
    />
  );
}

StructuredData.propTypes = {
  data: PropTypes.object.isRequired,
};

/**
 * Helper functions to generate structured data
 */

export function generateDiscussionThreadSchema(message, replies, baseUrl) {
  const authorProfileUrl = (user) => {
    if (!user) return `${baseUrl}/`;
    const handle = user.tetherHandle || user.tetherHAndle;
    if (handle) {
      const clean = handle.replace('@', '').replace(/\s+/g, '').toLowerCase();
      return `${baseUrl}/user/${clean}`;
    }
    const fallback = user.userId || user.userName;
    return fallback ? `${baseUrl}/user/${fallback}` : `${baseUrl}/`;
  };

  return {
    "@context": "https://schema.org",
    "@type": "DiscussionForumPosting",
    "headline": message.title || message.body?.substring(0, 100),
    "text": message.body,
    "datePublished": toISOStringSafe(message.timestamp),
    "author": {
      "@type": "Person",
      "name": message.userName || message.tetherHandle || "Anonymous",
      "url": authorProfileUrl(message)
    },
    "interactionStatistic": [
      {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/LikeAction",
        "userInteractionCount": message.likes || 0
      },
      {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/CommentAction",
        "userInteractionCount": replies?.length || message.replies || 0
      }
    ],
    "comment": replies?.slice(0, 10).map(reply => ({
      "@type": "Comment",
      "text": reply.text || reply.replyMessage || reply.message,
      "datePublished": toISOStringSafe(reply.timestamp),
      "author": {
        "@type": "Person",
        "name": reply.userName || reply.tetherHandle || "Anonymous",
        "url": authorProfileUrl(reply)
      }
    })) || [],
    "url": `${baseUrl}/talk/${message.id}`,
    "discussionUrl": `${baseUrl}/talk/${message.id}`,
    "about": message.categories?.map(category => ({
      "@type": "Thing",
      "name": category
    })) || []
  };
}

export function generateBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
}

export function generateWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "pplkpr",
    "description": "Build deeper connections with f8, your AI relationship companion",
    "url": "https://pplkpr.ai",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://pplkpr.ai/?search={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };
}
